/**
 * Trusted Apps Manager
 *
 * Manages list of trusted applications that don't require transaction confirmation
 * Data is stored in iframe's localStorage (isolated from parent domain)
 */

export interface TrustedApp {
  userId: string
  projectId: string
  origin: string
  trustedAt: number
  appName?: string
  appLogo?: string
}

// const STORAGE_KEY = 'passport-trusted-apps' // EMBARK TODO: deploy embark iframe
const STORAGE_KEY = 'lumia_passport_trusted_apps'

export class TrustedAppsManager {
  /**
   * Check if app is trusted by user
   */
  isTrusted(userId: string, projectId: string, origin: string): boolean {
    const trustedApps = this.getTrustedApps()
    const result = trustedApps.some(
      (app) => app.userId === userId && app.projectId === projectId && app.origin === origin
    )
    console.log('[TrustedApps] isTrusted check:', { userId, projectId, origin, result, totalApps: trustedApps.length })
    return result
  }

  /**
   * Add app to trusted list
   */
  addTrustedApp(userId: string, projectId: string, origin: string, appName?: string, appLogo?: string): void {
    console.log('[TrustedApps] Adding trusted app:', { userId, projectId, origin, appName })

    const trustedApps = this.getTrustedApps()

    // Check if already trusted
    const exists = trustedApps.some(
      (app) => app.userId === userId && app.projectId === projectId && app.origin === origin
    )

    if (exists) {
      console.log('[TrustedApps] App already trusted')
      return
    }

    // Add new trusted app
    trustedApps.push({
      userId,
      projectId,
      origin,
      trustedAt: Date.now(),
      appName,
      appLogo
    })

    this.saveTrustedApps(trustedApps)
    console.log('[TrustedApps] ✅ App added to trusted list')
  }

  /**
   * Remove app from trusted list
   */
  removeTrustedApp(userId: string, projectId: string, origin: string): void {
    console.log('[TrustedApps] Removing trusted app:', { userId, projectId, origin })

    const trustedApps = this.getTrustedApps()
    const filtered = trustedApps.filter(
      (app) => !(app.userId === userId && app.projectId === projectId && app.origin === origin)
    )

    this.saveTrustedApps(filtered)
    console.log('[TrustedApps] ✅ App removed from trusted list')
  }

  /**
   * Get all trusted apps for a user
   */
  getTrustedAppsForUser(userId: string): TrustedApp[] {
    console.log('[TrustedApps] Getting trusted apps for user:', userId)
    const trustedApps = this.getTrustedApps()
    console.log('[TrustedApps] Total apps in storage:', trustedApps.length, trustedApps)
    const filtered = trustedApps.filter((app) => app.userId === userId)
    console.log('[TrustedApps] Filtered apps for user:', filtered.length, filtered)
    return filtered
  }

  /**
   * Clear all trusted apps for a user
   */
  clearTrustedAppsForUser(userId: string): void {
    console.log('[TrustedApps] Clearing all trusted apps for user:', userId)

    const trustedApps = this.getTrustedApps()
    const filtered = trustedApps.filter((app) => app.userId !== userId)

    this.saveTrustedApps(filtered)
    console.log('[TrustedApps] ✅ Trusted apps cleared for user')
  }

  /**
   * Get all trusted apps from storage
   */
  private getTrustedApps(): TrustedApp[] {
    try {
      const data = localStorage.getItem(STORAGE_KEY)
      console.log('[TrustedApps] Raw storage data:', data)
      if (!data) {
        console.log('[TrustedApps] No data in storage')
        return []
      }

      const apps = JSON.parse(data)
      console.log('[TrustedApps] Parsed apps:', apps)
      return Array.isArray(apps) ? apps : []
    } catch (error) {
      console.error('[TrustedApps] Error reading trusted apps:', error)
      return []
    }
  }

  /**
   * Save trusted apps to storage
   */
  private saveTrustedApps(apps: TrustedApp[]): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(apps))
    } catch (error) {
      console.error('[TrustedApps] Error saving trusted apps:', error)
      throw new Error('Failed to save trusted apps')
    }
  }
}
